import React, { useEffect, useRef, useState } from 'react';
import { useVideoSource } from './helper';

type PreloadOption = 'none' | 'metadata' | 'auto';

interface MediaRendererProps {
  videoUrl?: string;
  videoMimeType?: string;
  audioUrl?: string;
  imageUrl?: string;
  embedUrl?: string;
  alt: string;
  wrapperClassName?: string;
  mediaClassName?: string;
  audioClassName?: string;
  videoPreload?: PreloadOption;
  audioPreload?: PreloadOption;
  videoPoster?: string;
  videoTimeoutMs?: number;
  onHasMediaChange?: (hasMedia: boolean) => void;
  onVideoCanPlay?: () => void;
}

function isMediaFailed(url: string | undefined, failedUrl: string | undefined): boolean {
  return Boolean(url) && failedUrl === url;
}

interface AdaptiveVideoProps {
  src: string;
  mimeType?: string;
  className?: string;
  poster?: string;
  preload: PreloadOption;
  onCanPlay?: () => void;
  onError: () => void;
}

const AdaptiveVideo: React.FC<AdaptiveVideoProps> = ({ src, mimeType, className, poster, preload, onCanPlay, onError }) => {
  const videoRef = useVideoSource(src, mimeType, onError);

  return (
    <video
      ref={videoRef}
      className={className}
      controls
      playsInline
      preload={preload}
      poster={poster}
      onCanPlay={onCanPlay}
      onError={onError}
    >
      <track kind="captions" />
    </video>
  );
};

export const MediaRenderer: React.FC<MediaRendererProps> = ({
  videoUrl,
  videoMimeType,
  audioUrl,
  imageUrl,
  embedUrl,
  alt,
  wrapperClassName,
  mediaClassName,
  audioClassName,
  videoPreload = 'none',
  audioPreload = 'none',
  videoPoster,
  videoTimeoutMs,
  onHasMediaChange,
  onVideoCanPlay,
}) => {
  const [failedVideoUrl, setFailedVideoUrl] = useState<string | undefined>(undefined);
  const [failedAudioUrl, setFailedAudioUrl] = useState<string | undefined>(undefined);
  const [failedImageUrl, setFailedImageUrl] = useState<string | undefined>(undefined);
  const videoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveVideoUrl = videoUrl;
  
  const videoFailed = isMediaFailed(effectiveVideoUrl, failedVideoUrl);
  const audioFailed = isMediaFailed(audioUrl, failedAudioUrl);
  const imageFailed = isMediaFailed(imageUrl, failedImageUrl);

  useEffect(() => {
    if (!effectiveVideoUrl || videoFailed || videoPreload === 'none') return;

    if (videoTimeoutMs) {
      videoTimeoutRef.current = setTimeout(() => {
        setFailedVideoUrl(effectiveVideoUrl);
      }, videoTimeoutMs);
    }

    return () => {
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
      }
    };
  }, [effectiveVideoUrl, videoFailed, videoTimeoutMs, videoPreload]);

  const hasMedia = Boolean(embedUrl) || (Boolean(effectiveVideoUrl) && !videoFailed) || (Boolean(audioUrl) && !audioFailed) || (Boolean(imageUrl) && !imageFailed);

  useEffect(() => {
    onHasMediaChange?.(hasMedia);
  }, [hasMedia, onHasMediaChange]);

  const handleVideoCanPlay = () => {
    if (videoTimeoutRef.current) {
      clearTimeout(videoTimeoutRef.current);
    }
    onVideoCanPlay?.();
  };

  if (!hasMedia) {
    return null;
  }

  let mediaElement: React.ReactElement | null = null;

  if (embedUrl) {
    mediaElement = (
      <iframe
        className={mediaClassName}
        src={embedUrl}
        loading="lazy"
        title={alt}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  } else if (effectiveVideoUrl && !videoFailed) {
    mediaElement = (
      <AdaptiveVideo
        src={effectiveVideoUrl}
        mimeType={videoMimeType}
        className={mediaClassName}
        poster={videoPoster}
        preload={videoPreload}
        onCanPlay={handleVideoCanPlay}
        onError={() => setFailedVideoUrl(effectiveVideoUrl)}
      />
    );
  } else if (imageUrl && !imageFailed) {
    mediaElement = (
      <img
        className={mediaClassName}
        src={imageUrl}
        decoding="async"
        loading="lazy"
        fetchPriority="low"
        alt={alt}
        onError={() => setFailedImageUrl(imageUrl)}
      />
    );
  } else if (audioUrl && !audioFailed) {
    mediaElement = (
      <video
        className={audioClassName ?? mediaClassName}
        src={audioUrl}
        controls
        playsInline
        preload={audioPreload}
        aria-label={alt}
        onError={() => setFailedAudioUrl(audioUrl)}
      >
        <track kind="captions" />
      </video>
    );
  }

  if (!mediaElement) {
    return null;
  }

  if (!wrapperClassName) {
    return mediaElement;
  }

  return <div className={wrapperClassName}>{mediaElement}</div>;
};
